'use client';

import { useEffect, useRef, useState } from 'react';

interface SloganSvgProps {
  className?: string;
  style?: React.CSSProperties;
}

export const SloganSvg = ({ className, style }: SloganSvgProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 },
    );

    const currentRef = svgRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      width="311"
      height="84"
      viewBox="0 0 311 84"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      role="img"
      aria-label="웰로 로컬페스타 2025 슬로건"
    >
      <defs>
        <style>
          {`
            .draw-path {
              stroke-dasharray: 1000;
              stroke-dashoffset: 1000;
              fill: transparent;
              stroke: rgba(255, 255, 255, 0.3);
              stroke-width: 0.5;
            }

            .draw-path.animate {
              animation: draw 2.2s ease-in-out forwards;
            }

            .draw-rect {
              opacity: 0;
            }

            .draw-rect.animate {
              animation: expandWidth 0.7s ease-in-out 1s forwards, fadeIn 0.3s ease-in 1s forwards;
            }

            @keyframes draw {
              to {
                stroke-dashoffset: 0;
                fill: white;
                stroke: rgba(255, 255, 255, 0);
              }
            }

            @keyframes expandWidth {
              from {
                width: 0;
              }
              to {
                width: 52;
              }
            }

            @keyframes fadeIn {
              to {
                opacity: 1;
              }
            }

            /* prefers-reduced-motion 지원 - 애니메이션 비활성화 */
            @media (prefers-reduced-motion: reduce) {
              .draw-path,
              .draw-rect {
                animation: none !important;
                stroke-dashoffset: 0;
                fill: white;
                stroke: rgba(255, 255, 255, 0);
                width: 52;
                opacity: 1;
              }
            }
          `}
        </style>
      </defs>

      <path
        className={`draw-path ${isVisible ? 'animate' : ''}`}
        d="M24.5938 8.65625V11.3438C24.5938 14.9375 24.5938 19.0312 23.4375 25.2188L18.875 24.75C19.9688 19.5 20.0469 15.5156 20.0938 12.2188H3.46875V8.65625H24.5938ZM26.9375 28.0312V31.6562H0.78125V28.0312H9.875V18.2812H14.3438V28.0312H26.9375ZM54.8438 10.25V13.875H51.2812V16.4688H54.8438V20.0938H51.2812V24.4375H46.7812V6.3125H51.2812V10.25H54.8438ZM41.8125 24.7188C47.75 24.7188 51.5938 26.6562 51.5938 29.875C51.5938 33.0625 47.75 35.0312 41.8125 35C35.75 35.0312 32 33.0625 32 29.875C32 26.6562 35.75 24.7188 41.8125 24.7188ZM41.8125 28.125C38.25 28.125 36.4375 28.625 36.4688 29.875C36.4375 31.0625 38.25 31.6875 41.8125 31.6875C45.2812 31.6875 47.0625 31.0625 47.0625 29.875C47.0625 28.625 45.2812 28.125 41.8125 28.125ZM45.4062 8.875V12.375H28.4062V8.875H34.6562V6.1875H39.1562V8.875H45.4062ZM36.9062 13.1875C41.0938 13.1875 44.0312 15.3125 44.0312 18.375C44.0312 21.4688 41.0938 23.5312 36.9062 23.5625C32.6875 23.5312 29.7188 21.4688 29.75 18.375C29.7188 15.3125 32.6875 13.1875 36.9062 13.1875ZM36.9062 16.4688C35.25 16.4688 34.125 17.0938 34.125 18.375C34.125 19.6562 35.25 20.25 36.9062 20.2188C38.5938 20.25 39.75 19.6562 39.75 18.375C39.75 17.0938 38.5938 16.4688 36.9062 16.4688ZM69.1562 6.6875C75.8438 6.71875 79.6562 8.4375 79.6875 11.5625C79.6562 14.75 75.8438 16.4688 69.1562 16.4688C62.4688 16.4688 58.625 14.75 58.625 11.5625C58.625 8.4375 62.4688 6.71875 69.1562 6.6875ZM69.1562 9.96875C65.125 9.96875 63.3438 10.5 63.3438 11.5625C63.3438 12.7812 65.125 13.1562 69.1562 13.1875C73.25 13.1562 75.0312 12.7812 75.0312 11.5625C75.0312 10.5 73.25 9.96875 69.1562 9.96875ZM82.25 17.625V21.1875H56.0938V17.625H82.25ZM79.125 22.5625V30.1562H63.5V31.4688H79.8125V34.8438H59.0625V27.0312H74.6562V25.8125H59.0625V22.5625H79.125ZM51.2812 48.3125V54.5938H55V58.2812H51.2812V64.3438H46.7812V48.3125H51.2812ZM42.2812 50.0625V53.625H33.9688V59.9062C38.9688 59.8906 41.6875 59.6719 44.6875 59.0625L45.1562 62.5938C41.6875 63.4062 38.4062 63.5 31.9375 63.5H29.4688V50.0625H42.2812ZM51.2812 65.4688V76.6562H32.3438V65.4688H51.2812ZM36.7812 69.0312V73.1562H46.8438V69.0312H36.7812ZM78.8438 48.3125V58.75H82.9062V62.4062H78.8438V77.0625H74.3438V48.3125H78.8438ZM70.6562 50.9375V54.5625H61.6875V66.4688C65.5469 66.4375 68.875 66.2031 72.5 65.5938L72.9688 69.3438C68.5625 70.0938 64.5938 70.1875 59.6562 70.1875H57.1875V50.9375H70.6562Z"
      />

      <path
        className={`draw-path ${isVisible ? 'animate' : ''}`}
        style={{ animationDelay: '0.3s' }}
        d="M192.531 70.25V73.9062H166.312V70.25H177.156V66.25H169.25V56.375H185.156V53.6875H169.219V50.0938H189.594V59.875H173.75V62.625H190.25V66.25H181.594V70.25H192.531ZM209.062 49.5C209.062 57.4062 205.344 61.8438 196 63.9375L194.406 60.5312C198.016 59.7969 200.406 58.75 201.938 57.4375L195.219 57.9062L194.594 54.5938L204.031 54.2812C204.141 53.8594 204.234 53.4062 204.281 52.9375H196.062V49.5H209.062ZM218.25 48.3125V62.7188H213.781V57.8438H209.125V54.25H213.781V48.3125H218.25ZM218.25 63.7188V71.75H203.5V73.1875H218.781V76.6875H199.094V68.5625H213.781V67.1562H199.031V63.7188H218.25ZM234.688 48.6875C241.375 48.7188 245.188 50.4375 245.219 53.5625C245.188 56.75 241.375 58.4688 234.688 58.4688C228 58.4688 224.156 56.75 224.156 53.5625C224.156 50.4375 228 48.7188 234.688 48.6875ZM234.688 51.9688C230.656 51.9688 228.875 52.5 228.875 53.5625C228.875 54.7812 230.656 55.1562 234.688 55.1875C238.781 55.1562 240.562 54.7812 240.562 53.5625C240.562 52.5 238.781 51.9688 234.688 51.9688ZM247.781 59.625V63.1875H221.625V59.625H247.781ZM244.656 64.5625V72.1562H229.031V73.4688H245.344V76.8438H224.594V69.0312H240.188V67.8125H224.594V64.5625H244.656ZM280.531 48.3125V67.3438H276.031V48.3125H280.531ZM272.969 66.4688C272.969 69.375 275.469 72.5 281.938 73.375L280.156 76.9062C275.484 76.1719 272.312 74.2188 270.625 71.5938C268.906 74.2188 265.734 76.1719 261.094 76.9062L259.344 73.375C265.781 72.5312 268.281 69.3438 268.281 66.4688V65.9688H272.969V66.4688ZM264.75 50.0312C269.25 50.0625 272.688 52.9688 272.688 57.0938C272.688 61.25 269.25 64.2188 264.75 64.2188C260.25 64.2188 256.781 61.25 256.812 57.0938C256.781 52.9688 260.25 50.0625 264.75 50.0312ZM264.75 53.875C262.656 53.875 261.188 55 261.188 57.0938C261.188 59.2188 262.656 60.4062 264.75 60.4062C266.781 60.4062 268.219 59.2188 268.219 57.0938C268.219 55 266.781 53.875 264.75 53.875ZM306.844 48.3125V58.75H310.906V62.4062H306.844V77.0625H302.344V48.3125H306.844ZM298.656 50.9375V54.5625H289.688V66.4688C293.547 66.4375 296.875 66.2031 300.5 65.5938L300.969 69.3438C296.562 70.0938 292.594 70.1875 287.656 70.1875H285.188V50.9375H298.656Z"
      />

      <rect
        className={`draw-rect ${isVisible ? 'animate' : ''}`}
        x="98"
        y="59"
        width="52"
        height="3"
        fill="white"
      />
    </svg>
  );
};
